// Journal suggestions for Discover — Suggested for you + Write catalog.
// Cross-device: ranked on the Worker from cleartext signals (same privacy model as Daily Wisdom).

import { apiGet } from './client';

export type SuggestionRoute = 'composer' | 'chat';
export type SuggestionCategory =
  | 'practice'
  | 'desire'
  | 'inner'
  | 'dream'
  | 'life'
  | 'contextual';

export interface JournalSuggestion {
  id: string;
  title: string;
  subtitle: string;
  seedTitle: string;
  seedBody: string;
  chatPrompt: string;
  category: SuggestionCategory;
  reason: string;
  route: SuggestionRoute;
  source: 'catalog' | 'pattern' | 'dream_theme';
}

export interface TodaySuggestions {
  suggestions: JournalSuggestion[];
  updatesInSeconds: number;
}

interface SuggestionRow {
  id?: string;
  title?: string;
  subtitle?: string;
  seed_title?: string;
  seed_body?: string;
  chat_prompt?: string;
  category?: string;
  reason?: string;
  route?: string;
  source?: string;
}

function toSuggestion(row: SuggestionRow): JournalSuggestion | null {
  const id = (row.id ?? '').trim();
  const title = (row.title ?? '').trim();
  if (!id || !title) return null;
  return {
    id,
    title,
    subtitle: (row.subtitle ?? '').trim(),
    seedTitle: (row.seed_title ?? '').trim() || title,
    seedBody: (row.seed_body ?? '').trim(),
    chatPrompt: (row.chat_prompt ?? '').trim(),
    category: (row.category as SuggestionCategory) || 'practice',
    reason: (row.reason ?? '').trim(),
    route: row.route === 'chat' ? 'chat' : 'composer',
    source:
      row.source === 'pattern' || row.source === 'dream_theme' ? row.source : 'catalog',
  };
}

export async function fetchJournalSuggestionsToday(
  signal?: AbortSignal,
): Promise<TodaySuggestions> {
  const res = await apiGet<{
    suggestions?: SuggestionRow[];
    updates_in_seconds?: number;
  }>('/api/journal-suggestions/today', signal);

  return {
    suggestions: (res.suggestions ?? [])
      .map(toSuggestion)
      .filter((s): s is JournalSuggestion => s != null),
    updatesInSeconds: Number(res.updates_in_seconds ?? 0),
  };
}

export async function fetchJournalCatalog(signal?: AbortSignal): Promise<JournalSuggestion[]> {
  const res = await apiGet<{ templates?: SuggestionRow[] }>(
    '/api/journal-suggestions/catalog',
    signal,
  );
  return (res.templates ?? [])
    .map(toSuggestion)
    .filter((s): s is JournalSuggestion => s != null);
}

export const WRITE_CATEGORIES: { id: SuggestionCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'practice', label: 'Practices' },
  { id: 'desire', label: 'Desire' },
  { id: 'inner', label: 'Inner work' },
  { id: 'dream', label: 'Dreams' },
  { id: 'life', label: 'Life' },
];
